import { useEffect, useMemo, useState } from "react";
import { PlaySquare, Sparkles, Loader2, Copy, Check, Upload } from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";
import {
  generateShortFromYoutube,
  getModels,
} from "../lib/api.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { resolvePromptText } from "../lib/promptText.js";
import { useI18n } from "../lib/i18n.jsx";

const DEFAULT_CHAT_MODEL = "nvidia/nemotron-3.5-lightning-30b-a3b";
const MAX_UPLOAD_MB = 70;
const FORMAT_OPTIONS = [
  { id: "ranking", label: "Ranking list (Top 5)" },
  { id: "explainer", label: "Explainer" },
  { id: "myth-vs-fact", label: "Myth vs Fact" },
  { id: "before-after", label: "Before / After" },
  { id: "mistakes-tips", label: "3 Mistakes / 3 Tips" },
];

export default function ShortsStudio() {
  const { t } = useI18n();
  const [chatModels, setChatModels] = useState([]);
  const [chatModel, setChatModel] = useState(DEFAULT_CHAT_MODEL);
  const [sourceVideoDataUrl, setSourceVideoDataUrl] = useState("");
  const [sourceFileName, setSourceFileName] = useState("");
  const [format, setFormat] = useState(FORMAT_OPTIONS[0].id);
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("Increase watch time and shares");
  const [durationSeconds, setDurationSeconds] = useState(30);
  const [variantCount, setVariantCount] = useState(3);
  const [burnCaptions, setBurnCaptions] = useState(true);
  const [trackingMode, setTrackingMode] = useState("smart");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rawOutput, setRawOutput] = useState("");
  const [plan, setPlan] = useState(null);
  const [videoSrc, setVideoSrc] = useState("");
  const [variants, setVariants] = useState([]);
  const [sourceMeta, setSourceMeta] = useState(null);

  useEffect(() => {
    getModels()
      .then((m) => {
        const nextChat = m.chat || [];
        setChatModels(nextChat);
        if (nextChat.length > 0) setChatModel(nextChat[0].id);
      })
      .catch(() => {});
  }, []);

  const fullScript = useMemo(() => {
    if (!plan) return "";
    const lines = Array.isArray(plan.script)
      ? plan.script
          .map((item) => {
            const start = Number(item.start_s || 0);
            const end = Number(item.end_s || 0);
            return `[${start.toFixed(1)}s-${end.toFixed(1)}s] ${item.line || ""}`;
          })
          .filter(Boolean)
      : [];
    const hashtags = Array.isArray(plan.hashtags) ? plan.hashtags.join(" ") : "";
    return [
      `Title: ${plan.short_title || ""}`,
      `Hook: ${plan.hook || ""}`,
      "",
      ...lines,
      "",
      `CTA: ${plan.cta || ""}`,
      hashtags ? `Hashtags: ${hashtags}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [plan]);

  const generate = async (notesOverride) => {
    if (loading) return;
    if (!sourceVideoDataUrl) {
      setError("Upload a source clip first.");
      return;
    }
    setLoading(true);
    setError(null);
    setStatus("Generating short video...");
    setRawOutput("");
    setPlan(null);
    setVideoSrc("");
    setVariants([]);
    setSourceMeta(null);

    const mergedNotes = resolvePromptText(notesOverride, notes);
    setNotes(mergedNotes);

    try {
      const result = await generateShortFromYoutube(
        {
          model: chatModel,
          sourceVideo: sourceVideoDataUrl,
          sourceFileName,
          format,
          audience,
          goal,
          durationSeconds: Number(durationSeconds) || 30,
          variantCount: Number(variantCount) || 3,
          burnCaptions,
          trackingMode,
          notes: mergedNotes,
        },
        {
          onStatus: setStatus,
        }
      );
      const nextVariants = Array.isArray(result?.variants) ? result.variants : [];
      setVariants(nextVariants);
      setPlan(nextVariants[0]?.plan || result?.plan || null);
      setVideoSrc(nextVariants[0]?.video || result?.video || "");
      setSourceMeta(result?.source || null);
      setRawOutput(JSON.stringify(result?.variants || result?.plan || {}, null, 2));
      setStatus(
        nextVariants.length > 1
          ? `Generated ${nextVariants.length} short variants.`
          : "Short video ready."
      );
    } catch (e) {
      setError(e.message || "Failed to generate Shorts strategy.");
    } finally {
      setLoading(false);
    }
  };

  const onSourceFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setError("Please upload a video file.");
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(
        `Please upload a clip under ${MAX_UPLOAD_MB}MB. Larger videos can exceed the hosted API upload limit.`
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSourceVideoDataUrl(String(reader.result || ""));
      setSourceFileName(file.name || "Uploaded Clip");
      setError(null);
    };
    reader.onerror = () => setError("Could not read the uploaded video.");
    reader.readAsDataURL(file);
  };

  const copyPlan = async () => {
    const text = fullScript.trim() || rawOutput.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Could not copy to clipboard. Please copy manually.");
    }
  };

  usePendingTask("/shorts", (task) => {
    setTaskBanner(task.rawPrompt || task.prompt);
    setNotes(task.prompt || "");
    generate(task.prompt || "");
  });

  const taskDone = useHomeTaskDone(loading, error, Boolean(taskBanner));

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader
        icon={PlaySquare}
        title={t("nav.shortsStudio", "Shorts Studio")}
        subtitle={t(
          "shorts.subtitle",
          "Upload a source clip and turn it into winning short-video formats like rankings and explainers."
        )}
      />

      <TaskBanner
        label={t("nav.shortsStudio", "Shorts Studio")}
        prompt={taskBanner}
        running={loading}
        error={error}
        completed={taskDone}
      />

      <GenerationProgress
        active={loading}
        message={status}
        hint={t(
          "shorts.hint",
          "This runs transcription, auto-clip selection, and vertical MP4 rendering from your uploaded clip."
        )}
        className="mx-4 md:mx-10 mt-3"
      />

      <div className="mx-4 md:mx-10 mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
        <p className="text-sm font-semibold text-white/90">
          {t("shorts.uploadNoteTitle", "Shorts Studio upload workflow")}
        </p>
        <p className="text-sm text-white/65 mt-1 leading-relaxed">
          {t(
            "shorts.uploadNoteBody",
            "Download YouTube videos yourself with your preferred tool, upload the clip here, and AI will transform it into short-form variants."
          )}
        </p>
      </div>

      <div className="flex-1 px-4 md:px-10 py-6 grid lg:grid-cols-[390px,1fr] gap-6 max-w-7xl mx-auto w-full">
        <aside className="card space-y-4 h-fit">
          <div>
            <label className="label">Source clip upload</label>
            <label className="flex items-center justify-center gap-2 border border-dashed border-white/15 rounded-md p-3 text-xs text-white/70 cursor-pointer hover:bg-white/[0.03]">
              <Upload className="h-3.5 w-3.5" />
              {sourceFileName ? sourceFileName : "Upload MP4/MOV/WebM"}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => onSourceFile(e.target.files?.[0])}
              />
            </label>
            {sourceVideoDataUrl && (
              <video
                src={sourceVideoDataUrl}
                controls
                className="mt-2 w-full rounded-lg border border-white/10"
              />
            )}
          </div>

          <div>
            <label className="label">{t("shorts.format", "Winning format")}</label>
            <select value={format} onChange={(e) => setFormat(e.target.value)} className="input">
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">{t("shorts.audience", "Audience")}</label>
            <input
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. startup founders and creators"
              className="input"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) =>
                setAudience((prev) => appendTranscript(prev, text))
              }
              onError={(msg) => setError(msg)}
            />
          </div>

          <div>
            <label className="label">{t("shorts.goal", "Goal")}</label>
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Increase watch time"
              className="input"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) => setGoal((prev) => appendTranscript(prev, text))}
              onError={(msg) => setError(msg)}
            />
          </div>

          <div>
            <label className="label">{t("shorts.duration", "Target duration (seconds)")}</label>
            <input
              value={durationSeconds}
              onChange={(e) => setDurationSeconds(e.target.value)}
              type="number"
              min={10}
              max={90}
              className="input"
            />
          </div>

          <div>
            <label className="label">Variants per source</label>
            <select
              value={variantCount}
              onChange={(e) => setVariantCount(Number(e.target.value))}
              className="input"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                checked={burnCaptions}
                onChange={(e) => setBurnCaptions(e.target.checked)}
              />
              Burn captions
            </label>
            <label className="flex items-center gap-2 text-sm text-white/80">
              <span className="text-xs text-white/55">Tracking</span>
              <select
                value={trackingMode}
                onChange={(e) => setTrackingMode(e.target.value)}
                className="input !py-1.5 !text-xs"
              >
                <option value="smart">Smart (dynamic)</option>
                <option value="center">Center crop</option>
              </select>
            </label>
          </div>

          <div>
            <label className="label">{t("shorts.notes", "Extra direction")}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tone, pacing, references, CTA preferences..."
              className="textarea min-h-[100px]"
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(text) => setNotes((prev) => appendTranscript(prev, text))}
              onError={(msg) => setError(msg)}
            />
          </div>

          <div>
            <label className="label">{t("shorts.chatModel", "Chat model")}</label>
            <select
              value={chatModel}
              onChange={(e) => setChatModel(e.target.value)}
              className="input"
            >
              {chatModels.length === 0 && <option value={chatModel}>{chatModel}</option>}
              {chatModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <button onClick={() => generate()} disabled={loading} className="btn-primary w-full">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {t("shorts.generate", "Generate Shorts Strategy")}
          </button>
        </aside>

        <section className="space-y-4 min-w-0">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold">
                {t("shorts.output", "Shorts Plan Output")}
              </h3>
              <button onClick={copyPlan} className="btn-secondary" disabled={!rawOutput.trim()}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t("home.share.copied", "Copied") : t("common.copy", "Copy")}
              </button>
            </div>

            {plan ? (
              <div className="space-y-3 text-sm">
                {sourceMeta && (
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/70">
                    Source: {sourceMeta.title || sourceFileName || "Uploaded clip"}
                    {sourceMeta.sourceType === "youtube" && sourceMeta.author
                      ? ` by ${sourceMeta.author}`
                      : ""}
                    {sourceMeta.durationSeconds
                      ? ` (${Math.round(sourceMeta.durationSeconds)}s)`
                      : ""}
                  </div>
                )}
                <div>
                  <p className="text-white/45 text-xs uppercase tracking-wide">Title</p>
                  <p className="font-medium text-white/90">{plan.short_title || "-"}</p>
                </div>
                <div>
                  <p className="text-white/45 text-xs uppercase tracking-wide">Hook</p>
                  <p className="text-white/85">{plan.hook || "-"}</p>
                </div>
                <div>
                  <p className="text-white/45 text-xs uppercase tracking-wide mb-1.5">Script</p>
                  <div className="space-y-1.5">
                    {(Array.isArray(plan.script) ? plan.script : []).map((line, idx) => (
                      <div key={`${idx}-${line.start_s || 0}`} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                        <p className="text-[11px] text-white/45">
                          {Number(line.start_s || 0).toFixed(1)}s -{" "}
                          {Number(line.end_s || 0).toFixed(1)}s
                        </p>
                        <p className="text-white/85">{line.line || ""}</p>
                        <p className="text-[12px] text-consensus-200/90 mt-1">
                          Visual: {line.visual_direction || ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-white/45 text-xs uppercase tracking-wide">CTA</p>
                    <p className="text-white/85">{plan.cta || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/45 text-xs uppercase tracking-wide">Hashtags</p>
                    <p className="text-white/85">
                      {Array.isArray(plan.hashtags) ? plan.hashtags.join(" ") : "-"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-white/15 px-4 py-10 text-center text-white/45 text-sm">
                {t(
                  "shorts.empty",
                  "Your generated Shorts strategy appears here after you run it."
                )}
              </div>
            )}

            {(variants.length > 0 || videoSrc) && (
              <div className="space-y-3">
                <p className="text-white/45 text-xs uppercase tracking-wide">Rendered MP4 variants</p>
                {(variants.length ? variants : [{ variantIndex: 1, variantLabel: "Variant 1", video: videoSrc }]).map(
                  (v) => (
                    <div key={v.variantIndex} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
                      <p className="text-sm font-medium text-white/85">{v.variantLabel || `Variant ${v.variantIndex}`}</p>
                      <video
                        src={v.video}
                        controls
                        className="w-full rounded-xl border border-white/10"
                      />
                      <a
                        href={v.video}
                        download={`short-variant-${v.variantIndex || 1}-${Date.now()}.mp4`}
                        className="btn-secondary inline-flex"
                      >
                        {t("common.download", "Download")}
                      </a>
                    </div>
                  )
                )}
              </div>
            )}

            {rawOutput && (
              <details className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <summary className="cursor-pointer text-sm text-white/75">
                  {t("shorts.raw", "Raw model output")}
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-white/70">
                  {rawOutput}
                </pre>
              </details>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              {error}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
